import Vue from "vue";
import InactivityWorker from "@/workers/inactivityWorker.js?sharedworker";
import {
    InactivityStateMachine,
    InactivityMessage,
    DEFAULT_GRACE_MS,
    DEFAULT_WARNING_MS,
    type InactivityConfig,
    type InactivityState,
} from "@/util/inactivity/inactivityStateMachine";
import { AuthProviderFactory } from "@/util/authProviderFactory";
import userInfoService from "@/util/userInfoService";

declare module "vue/types/vue" {
    interface Vue {
        /** Resets the inactivity countdown (e.g. from the warning modal's "Stay signed in" button). */
        $inactivityReset?: () => void;
        /** Immediately signs the user out (e.g. from the overlay's "Sign out now" button). */
        $inactivitySignOut?: () => void;
    }
}

/** Minimum gap between activity resets posted to the timer; throttles high-frequency mousemove. */
const ACTIVITY_THROTTLE_MS = 1000;
const CONFIG_FIELD = "FrontEndInactivityTimeoutMinutes";
/** Retries for loading the idle limit: a transient failure must not silently leave this
 *  security control disabled for the whole session. */
const CONFIG_LOAD_ATTEMPTS = 3;
const CONFIG_RETRY_DELAY_MS = 2000;

let initialized = false;

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Loads the configured idle limit (minutes), retrying a few times on transient failure. Returns the
 * minutes (0 = feature disabled), or null when it could not be loaded after every attempt. Fails
 * open (null -> disabled) rather than locking everyone out on a backend blip, but logs loudly so the
 * disabled-security-control state is not silent.
 */
async function loadTimeoutMinutes(app: Vue): Promise<number | null> {
    for (let attempt = 1; attempt <= CONFIG_LOAD_ATTEMPTS; attempt++) {
        try {
            const resp = await app.$http.get<number>(`/configuration/${CONFIG_FIELD}`);
            const minutes = Number(resp.data);
            // A 200 response with a non-numeric body is a real failure, not an intentional 0
            // (disabled): treat it like a thrown error so the retry/loud-warning path engages
            // instead of silently turning this security control off.
            if (!Number.isFinite(minutes)) {
                throw new Error(`Inactivity timeout: non-numeric configuration value ${JSON.stringify(resp.data)}`);
            }
            return minutes;
        } catch (err) {
            if (attempt === CONFIG_LOAD_ATTEMPTS) {
                console.warn(
                    `Inactivity timeout: failed to load configuration after ${CONFIG_LOAD_ATTEMPTS} attempts; feature disabled.`,
                    err,
                );
                return null;
            }
            await delay(CONFIG_RETRY_DELAY_MS);
        }
    }
    return null;
}

/**
 * Wires the inactivity timeout once, after authentication. Loads the configured idle limit (0/blank
 * disables the feature), runs the countdown in a SharedWorker so it is shared across all tabs (with
 * a per-tab fallback when SharedWorker is unavailable), resets it on mouse/keyboard activity, and
 * signs the user out when the timer expires.
 */
export async function inactivitySetup(app: Vue): Promise<void> {
    if (initialized) return;
    initialized = true;

    const timeoutMinutes = await loadTimeoutMinutes(app);
    if (timeoutMinutes === null) return; // unavailable after retries; fail open (disabled)
    if (timeoutMinutes <= 0) {
        console.debug("Inactivity timeout disabled (0 minutes).");
        return;
    }

    const config: InactivityConfig = {
        timeoutMs: timeoutMinutes * 60_000,
        // The state machine clamps this lead so a short timeout still gets an active period before
        // the warning; pass the desired default and let arm() own that policy.
        warningMs: DEFAULT_WARNING_MS,
        graceMs: DEFAULT_GRACE_MS,
    };

    const onState = (state: InactivityState): void => {
        app.$root.$data.inactivityState = state;
        if (state.phase === "signout") signOut(app);
    };

    // Prefer a SharedWorker so the countdown is a single source of truth across all tabs; fall back
    // to a per-tab timer when SharedWorker is unavailable.
    //  - postActivity: passive mouse/keyboard activity. The timer honors it only while "active", so
    //    passive activity cannot dismiss a visible warning. That guard lives in the timer, not in
    //    this tab, so a tab whose cached phase lags the worker cannot wrongly extend the session.
    //  - postReset: an explicit "Stay signed in"; honored while not locked.
    //  - postSync: recompute the phase from wall-clock; posted when the tab is refocused so a tab
    //    left idle in the background locks the instant it is looked at again, even if the worker's
    //    timers were throttled while it was hidden.
    let postActivity: () => void;
    let postReset: () => void;
    let postSync: () => void;
    if (window.SharedWorker) {
        const worker = new InactivityWorker();
        worker.port.onmessage = (e: MessageEvent) => {
            if (e.data?.[0] === InactivityMessage.State) onState(e.data[1] as InactivityState);
        };
        worker.port.start();
        worker.port.postMessage([InactivityMessage.Configure, config]);
        postActivity = () => worker.port.postMessage([InactivityMessage.Activity]);
        postReset = () => worker.port.postMessage([InactivityMessage.Reset]);
        postSync = () => worker.port.postMessage([InactivityMessage.Sync]);
    } else {
        const machine = new InactivityStateMachine(onState);
        machine.configure(config);
        postActivity = () => machine.activity();
        postReset = () => machine.reset();
        postSync = () => machine.evaluate();
    }

    Vue.prototype.$inactivityReset = postReset;
    Vue.prototype.$inactivitySignOut = () => signOut(app);

    // Report user activity to the timer. Throttled so a stream of mousemove events does not flood the
    // worker port; keydown covers typing, and mouse clicks generally co-occur with mousemove. The
    // timer itself ignores activity once the warning is showing (InactivityStateMachine.activity), so
    // this throttle is purely a traffic optimization, not the "can't dismiss the warning" guard.
    let lastActivity = 0;
    const onActivity = (): void => {
        const now = Date.now();
        if (now - lastActivity < ACTIVITY_THROTTLE_MS) return;
        lastActivity = now;
        postActivity();
    };
    window.addEventListener("mousemove", onActivity, { passive: true });
    window.addEventListener("keydown", onActivity, { passive: true });

    // When a backgrounded tab returns to the foreground, ask the timer to re-evaluate against
    // wall-clock time. If the idle period elapsed while the worker's timers were throttled, the
    // correct (locked/signout) phase is applied immediately rather than after a long-overdue timer.
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") postSync();
    });
}

/**
 * Signs the user out via the active auth provider using a full-page navigation, so the app
 * re-initializes on return and the SharedWorker-backed inactivity timer re-arms for the next
 * session. Redirect-based (not popup) sign-out also means each open tab redirects itself, instead
 * of every tab opening its own logout popup when the shared timer fires.
 */
function signOut(app: Vue): void {
    try {
        userInfoService.clearUserData();

        // Delegate to the configured provider's redirect logout (Cognito -> hosted-UI logout,
        // MSAL/OIDC -> logoutRedirect). Using the AuthProvider abstraction instead of branching on
        // the provider type keeps OIDC working and avoids a third copy of the provider switch.
        AuthProviderFactory.createProvider(app.$authProviderType).logoutRedirect(window.location.origin);
    } catch (err) {
        console.error("Inactivity timeout: sign-out failed.", err);
    }
}
